import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { registerPushNotifications, scheduleReminders } from '../lib/notifications';
import { initFirebase, requestNotificationPermission, onForegroundMessage } from '../lib/firebase';
import { Capacitor } from '@capacitor/core';

/**
 * Hook that initializes push notifications for the logged-in client.
 * - Registers for push on native (Capacitor) or web (Firebase)
 * - Loads protocol items and schedules local reminders
 * - Listens for foreground messages on web
 */
export function useNotifications() {
  const { user, role } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    if (!user || role !== 'client' || initialized.current) return;
    initialized.current = true;

    async function setup() {
      try {
        if (Capacitor.isNativePlatform()) {
          // Native: use Capacitor push notifications
          await registerPushNotifications(user.id);
        } else {
          // Web: use Firebase
          const fbReady = await initFirebase();
          if (fbReady) {
            const token = await requestNotificationPermission();
            if (token) {
              // Store web push token
              await supabase.auth.updateUser({
                data: { web_push_token: token },
              });
            }

            // Listen for foreground messages
            onForegroundMessage((payload) => {
              // Show browser notification
              if (Notification.permission === 'granted') {
                new Notification(payload.notification?.title || 'Correly', {
                  body: payload.notification?.body || '',
                  icon: '/correly-icon.svg',
                });
              }
            });
          }
        }

        // Load supplements and schedule local reminders
        const { data: clientData } = await supabase
          .from('clients')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (clientData) {
          const { data: supps } = await supabase
            .from('supplements')
            .select('*')
            .eq('client_id', clientData.id)
            .eq('is_active', true);

          if (supps?.length > 0) {
            await scheduleReminders(supps);
          }
        }
      } catch (err) {
        console.error('Notification setup error:', err);
      }
    }

    setup();
  }, [user, role]);
}
